document.documentElement.classList.remove('no-js');
document.documentElement.classList.add('js');

const desktopQuery = window.matchMedia('(min-width: 768px)');

// Retain the existing hook; cards now wrap intrinsically without text-height balancing.
class HcLimitedCardGrid extends HTMLElement {}
customElements.define('hc-limited-card-grid', HcLimitedCardGrid);

// One reusable label preserves original content and accessible names, including cloned menus.
function bind_link_labels(root) {
    const label_selector = '.hc-link-label, .hc-cta-line, .cta-line, .hc-policy-link-label, .hc-menu-action__label, .hc-discovery-card__title, .hc-link-card__title, .proof-photo-zoom, .hc-artwork-stage > span';
    root.querySelectorAll(label_selector).forEach(label => {
        if (label.querySelector('.hc-link-label') || label.querySelector('.hc-link-label__text')) return;
        label.classList.add('hc-link-label');
        const text = document.createElement('span');
        text.className = 'hc-link-label__text';
        text.append(...label.childNodes);
        const reveal = text.cloneNode(true);
        reveal.className = 'hc-link-label__reveal';
        reveal.setAttribute('aria-hidden', 'true');
        reveal.querySelectorAll('[id]').forEach(node => node.removeAttribute('id'));
        label.append(text, reveal);
    });
    // Wrap direct text nodes in prose/menu links, leaving icons and nested controls outside.
    root.querySelectorAll('a:not(.hc-icon-button), button.hc-menu-link, button.hc-link, button.reset').forEach(action => {
        if (action.querySelector('.hc-link-label')) return;
        if (!action.querySelector('img, svg, input, button, select, textarea, .hc-badge') && action.textContent.trim()) {
            const label = document.createElement('span');
            label.className = 'hc-link-label';
            label.append(...action.childNodes);
            action.append(label);
            return;
        }
        [...action.childNodes].forEach(node => {
            if (node.nodeType !== Node.TEXT_NODE || !node.textContent.trim()) return;
            const label = document.createElement('span');
            label.className = 'hc-link-label';
            node.replaceWith(label);
            label.append(node);
        });
    });
    root.querySelectorAll('.hc-link-label:not(:has(.hc-link-label__text))').forEach(label => {
        const text = document.createElement('span');
        text.className = 'hc-link-label__text';
        text.append(...label.childNodes);
        const reveal = text.cloneNode(true);
        reveal.className = 'hc-link-label__reveal';
        reveal.setAttribute('aria-hidden', 'true');
        label.append(text, reveal);
    });
    root.querySelectorAll('.hc-link-label').forEach(label => {
        if (!label.style.getPropertyValue('--hc-link-ink')) label.style.setProperty('--hc-link-ink', getComputedStyle(label).color);
    });
}
bind_link_labels(document);
const link_label_observer = new MutationObserver(() => bind_link_labels(document));
link_label_observer.observe(document.body, { childList: true, subtree: true });

class HcOpenSignup extends HTMLElement {
  connectedCallback() {
    // Give the actual cloned form its own native fragment and associated input ID.
    const form = this.querySelector('form');
    if (!form) return;
    const placement = this.closest('.hc-drawer') ? 'mobile' : 'desktop';
    form.dataset.openSignupBase ||= form.id;
    form.id = `${form.dataset.openSignupBase}-${placement}`;
    const input = form.querySelector('[name="contact[email]"]');
    input.id = `${form.id}Email`;
    form.querySelector('label').htmlFor = input.id;

    // Keep Shopify's native customer endpoint and POST, changing only its fragment.
    const action = new URL(form.action, window.location.href);
    action.hash = form.id;
    form.action = action.href;
    const returnTo = form.querySelector('[name="return_to"]');
    if (returnTo) returnTo.value = `${window.location.pathname}${window.location.search}#${form.id}`;

    // Shopify supplies receipt/errors; expose them only in the submitting instance.
    const submitted = window.location.hash === `#${form.id}`;
    this.querySelectorAll('.hc-newsletter-form__message').forEach((message) => {
      message.hidden = !submitted;
    });
  }
}
customElements.define('hc-open-signup', HcOpenSignup);

document.querySelectorAll('[data-header-tooltip]').forEach((icon) => {
  // Restore hover/focus eligibility after leaving an explicitly dismissed tooltip.
  const resetTooltip = () => icon.removeAttribute('data-tooltip-dismissed');
  icon.addEventListener('pointerleave', resetTooltip);
  icon.addEventListener('blur', resetTooltip);
});
document.addEventListener('keydown', (event) => {
  // Escape dismisses the explanation without changing link focus or action.
  if (event.key !== 'Escape') return;
  document.querySelectorAll('[data-header-tooltip]').forEach((icon) => {
    if (icon.matches(':hover, :focus')) icon.setAttribute('data-tooltip-dismissed', '');
  });
});

class HcDisclosure extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-disclosure-button]');
    this.panel = this.querySelector('[data-disclosure-panel]');
    this.rootPanel = this.querySelector('.hc-drawer__root');
    this.backdrop = this.querySelector('[data-mobile-backdrop]');
    this.expanders = Array.from(this.querySelectorAll('[data-mobile-expand]'));
    if (!this.button || !this.panel) return;
    this.button.addEventListener('click', () => this.button.getAttribute('aria-expanded') === 'true' ? this.close({ focusButton: true }) : this.open());
    this.expanders.forEach(button => button.addEventListener('click', () => this.expand(button, button.getAttribute('aria-expanded') !== 'true')));
    this.backdrop?.addEventListener('click', () => this.close({ focusButton: true }));
    this.addEventListener('keydown', event => {
      if (this.panel.hidden || this.panel.inert) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        const owner = document.activeElement.closest('.hc-mobile-nav__group.is-expanded,.hc-mobile-nav__category.is-expanded');
        const button = owner?.querySelector(':scope > [data-mobile-expand]') || this.expanders.find(item => item.getAttribute('aria-expanded') === 'true');
        if (button) { this.expand(button, false); button.focus({ preventScroll: true }); }
        else this.close({ focusButton: true });
      }
      if (event.key === 'Tab' && this.rootPanel) {
        const targets = Array.from(this.querySelectorAll('a[href],button,input,select,textarea,[tabindex="0"]')).filter(item => !item.disabled && item.tabIndex >= 0 && !item.closest('[inert],[hidden]') && item.getClientRects().length);
        const first = targets[0], last = targets.at(-1);
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus({ preventScroll: true }); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus({ preventScroll: true }); }
      }
    });
    document.addEventListener('click', event => { if (!this.panel.hidden && !this.contains(event.target)) this.close({ focusButton: true }); });
    if (this.rootPanel) desktopQuery.addEventListener('change', event => { if (event.matches) this.close({ focusButton: false }); });
  }

  expand(button, expanded) {
    const content = this.querySelector('#' + button.getAttribute('aria-controls'));
    if (!content) return;
    if (!expanded && content.contains(document.activeElement)) button.focus({ preventScroll: true });
    button.setAttribute('aria-expanded', String(expanded));
    button.parentElement.classList.toggle('is-expanded', expanded);
    content.inert = !expanded;
  }

  lockPage() {
    if (!this.rootPanel || this.pageLock) return;
    this.pageLock = { y: window.scrollY, style: document.body.getAttribute('style') };
    const header = this.closest('.hc-header');
    header?.classList.add('is-mobile-menu-open');
    Object.assign(document.body.style, { position: 'fixed', top: `-${this.pageLock.y}px`, width: '100%', overflow: 'hidden' });
    this.syncViewport = () => {
      const bottom = header?.getBoundingClientRect().bottom || 0;
      const height = window.visualViewport?.height || window.innerHeight;
      this.panel.style.top = `${bottom}px`;
      this.panel.style.height = `${Math.max(0, height - bottom)}px`;
      if (this.backdrop) this.backdrop.style.top = `${bottom}px`;
    };
    this.syncViewport();
    window.addEventListener('resize', this.syncViewport);
    window.visualViewport?.addEventListener('resize', this.syncViewport);
  }

  unlockPage() {
    if (!this.pageLock) return;
    const saved = this.pageLock;
    this.pageLock = null;
    this.closest('.hc-header')?.classList.remove('is-mobile-menu-open');
    window.removeEventListener('resize', this.syncViewport);
    window.visualViewport?.removeEventListener('resize', this.syncViewport);
    this.panel.style.removeProperty('height');
    this.panel.style.removeProperty('top');
    if (saved.style === null) document.body.removeAttribute('style');
    else document.body.setAttribute('style', saved.style);
    window.scrollTo(0, saved.y);
  }

  open() {
    window.clearTimeout(this.hideDelay);
    this.expanders.forEach(button => this.expand(button, false));
    this.lockPage();
    this.panel.inert = false;
    this.button.setAttribute('aria-expanded', 'true');
    this.panel.hidden = false;
    if (this.backdrop) this.backdrop.hidden = false;
    if (this.rootPanel) this.rootPanel.scrollTop = 0;
    this.panel.getBoundingClientRect();
    window.cancelAnimationFrame(this.openFrame);
    this.openFrame = window.requestAnimationFrame(() => { if (this.button.getAttribute('aria-expanded') === 'true') { this.panel.classList.add('is-open'); this.backdrop?.classList.add('is-open'); } });
  }

  close({ focusButton = false } = {}) {
    window.cancelAnimationFrame(this.openFrame);
    window.clearTimeout(this.hideDelay);
    if (focusButton || this.panel.contains(document.activeElement)) this.button.focus({ preventScroll: true });
    this.button.setAttribute('aria-expanded', 'false');
    this.expanders?.forEach(button => this.expand(button, false));
    this.panel.inert = true;
    this.panel.classList.remove('is-open');
    this.backdrop?.classList.remove('is-open');
    this.unlockPage();
    this.hideDelay = window.setTimeout(() => { if (this.button.getAttribute('aria-expanded') !== 'true') { this.panel.hidden = true; if (this.backdrop) this.backdrop.hidden = true; } }, 260);
  }
}

customElements.define('hc-disclosure', HcDisclosure);

class HcMegaMenu extends HTMLElement {
  connectedCallback() {
    this.triggers = Array.from(this.querySelectorAll('[data-mega-menu-trigger]'));
    this.closeTriggers = Array.from(this.querySelectorAll('[data-mega-menu-close]'));
    this.panel = this.querySelector('[data-mega-menu-panel]');
    this.backdrop = this.querySelector('[data-mega-menu-backdrop]');
    this.contentTarget = this.querySelector('.hc-mega-menu__inner');
    this.templates = new Map(
      Array.from(this.querySelectorAll('template[data-mega-menu-template]')).map((template) => [
        template.dataset.megaMenuTemplate,
        template,
      ]),
    );
    this.handleKeydown = this.handleKeydown.bind(this);
    this.closeDelay = null;
    this.hideDelay = null;
    this.contentDelay = null;
    this.activeTrigger = null;
    this.suppressFocusOpen = false;
    this.animationDuration = 220;

    if (!this.triggers.length || !this.panel || !this.backdrop) return;

    // The fixed backdrop is a child, so leaving content for it does not leave the menu.
    this.backdrop.addEventListener('mouseenter', () => this.clearCloseDelay());
    this.backdrop.addEventListener('click', () => this.close());

    this.triggers.forEach((trigger) => {
      // A menu-only button must also open on explicit mouse or keyboard activation.
      if (trigger.tagName === 'BUTTON') {
        trigger.addEventListener('click', () => {
          if (desktopQuery.matches) this.open(trigger);
        });
      }
      trigger.addEventListener('mouseenter', () => {
        if (desktopQuery.matches) this.open(trigger);
      });

      trigger.addEventListener('focus', () => {
        if (this.suppressFocusOpen) return;
        if (desktopQuery.matches) this.open(trigger);
      });
    });

    this.closeTriggers.forEach((trigger) => {
      trigger.addEventListener('mouseenter', () => {
        if (desktopQuery.matches) this.close();
      });

      trigger.addEventListener('focus', () => {
        if (desktopQuery.matches) this.close();
      });
    });

    this.addEventListener('mouseenter', () => {
      if (!desktopQuery.matches) return;
      this.clearCloseDelay();
    });

    // The panel sits below the header padding: treat that bridge as part of the menu.
    this.handlePointerMove = (event) => {
      if (!desktopQuery.matches || this.panel.hidden || !this.activeTrigger) return;
      const bar = this.querySelector('.hc-header__nav').getBoundingClientRect();
      const panel = this.panel.getBoundingClientRect();
      const inside = event.clientX >= panel.left && event.clientX <= panel.right &&
        event.clientY >= bar.top && event.clientY <= panel.bottom;
      if (inside) this.clearCloseDelay();
      else this.scheduleClose(0);
    };
    document.addEventListener('pointermove', this.handlePointerMove);
    this.panel.addEventListener('mouseenter', () => this.clearCloseDelay());

    this.addEventListener('focusout', (event) => {
      if (this.contains(event.relatedTarget) || this.panel.matches(':hover')) return;
      this.scheduleClose(0);
    });

    desktopQuery.addEventListener('change', () => {
      if (!desktopQuery.matches) this.close();
    });
  }

  disconnectedCallback() {
    document.removeEventListener('pointermove', this.handlePointerMove);
    document.removeEventListener('keydown', this.handleKeydown);
    this.clearCloseDelay();
  }

  open(trigger) {
    this.clearCloseDelay();
    window.clearTimeout(this.hideDelay);
    this.setActiveTrigger(trigger);
    this.renderContent(trigger);
    this.panel.setAttribute('aria-label', trigger.querySelector('.hc-link-label__text')?.textContent.trim() || trigger.textContent.trim());

    if (!this.panel.hidden) {
      this.panel.classList.add('is-open');
      this.backdrop.classList.add('is-open');
      this.scheduleContentReveal();
      document.addEventListener('keydown', this.handleKeydown);
      return;
    }

    this.panel.classList.remove('is-content-ready');
    this.panel.classList.remove('is-open');
    this.backdrop.classList.remove('is-open');
    this.panel.hidden = false;
    this.backdrop.hidden = false;
    // Ensure the tucked-up start state is painted before the open class transitions it down.
    this.panel.getBoundingClientRect();
    window.requestAnimationFrame(() => {
      this.panel.classList.add('is-open');
      this.backdrop.classList.add('is-open');
      this.scheduleContentReveal();
    });
    document.addEventListener('keydown', this.handleKeydown);
  }

  scheduleContentReveal() {
    window.clearTimeout(this.contentDelay);
    this.contentDelay = window.setTimeout(() => {
      if (!this.panel.hidden && this.panel.classList.contains('is-open')) {
        this.panel.classList.add('is-content-ready');
      }
    }, this.animationDuration);
  }

  scheduleClose(delay = 0) {
    this.clearCloseDelay();
    this.closeDelay = window.setTimeout(() => {
      this.close();
    }, delay);
  }

  clearCloseDelay() {
    window.clearTimeout(this.closeDelay);
    this.closeDelay = null;
  }

  setActiveTrigger(trigger) {
    this.activeTrigger = trigger;
    this.triggers.forEach((item) => {
      item.setAttribute('aria-expanded', String(item === trigger));
    });
  }

  renderContent(trigger) {
    // Resolve the requested existing template without altering another menu's content.
    if (!this.contentTarget || !trigger) return;
    const templateName = trigger.dataset.megaMenuTemplate;
    const template = this.templates.get(templateName);
    if (!template) return;

    // CSS owns each menu's dimensions; retain an already mounted form and its input.
    this.dataset.activeMenu = templateName;
    if (this.contentTarget.dataset.activeTemplate === templateName) return;
    this.contentTarget.replaceChildren(template.content.cloneNode(true));
    this.contentTarget.dataset.activeTemplate = templateName;
  }

  clearActiveTrigger() {
    this.activeTrigger = null;
    this.triggers.forEach((item) => {
      item.setAttribute('aria-expanded', 'false');
    });
  }

  close({ focusTrigger = false } = {}) {
    this.clearCloseDelay();
    const triggerToFocus = this.activeTrigger;
    this.clearActiveTrigger();
    window.clearTimeout(this.contentDelay);
    this.panel.classList.remove('is-content-ready');
    if (this.panel.hidden) {
      this.backdrop.classList.remove('is-open');
      this.backdrop.hidden = true;
      if (focusTrigger && triggerToFocus) this.focusTriggerWithoutOpening(triggerToFocus);
      return;
    }

    this.panel.classList.remove('is-open');
    this.backdrop.classList.remove('is-open');
    document.removeEventListener('keydown', this.handleKeydown);

    window.clearTimeout(this.hideDelay);
    this.hideDelay = window.setTimeout(() => {
      if (!this.panel.classList.contains('is-open')) {
        this.panel.hidden = true;
        this.backdrop.hidden = true;
        this.panel.classList.remove('is-content-ready');
      }
    }, this.animationDuration);

    if (focusTrigger && triggerToFocus) this.focusTriggerWithoutOpening(triggerToFocus);
  }

  focusTriggerWithoutOpening(trigger) {
    this.suppressFocusOpen = true;
    trigger.focus();
    window.setTimeout(() => {
      this.suppressFocusOpen = false;
    }, 0);
  }

  handleKeydown(event) {
    if (event.key !== 'Escape') return;
    this.close({ focusTrigger: true });
  }
}

customElements.define('hc-mega-menu', HcMegaMenu);

function restoreOpenSignupResponse() {
  // Reopen only a server-rendered response for this exact Open form instance.
  const template = document.querySelector('template[data-mega-menu-template="prints"]');
  const form = template?.content.querySelector('hc-open-signup form');
  if (!form?.querySelector('.hc-newsletter-form__message')) return;
  const placement = desktopQuery.matches ? 'desktop' : 'mobile';
  if (window.location.hash !== `#${form.id}-${placement}` || placement === 'mobile') return;

  // Shared Shopify customer receipts belong only to this Open submission, not other lists.
  const surfaces = [document, ...Array.from(document.querySelectorAll('template[data-mega-menu-template]'), item => item.content)];
  surfaces.forEach((surface) => {
    surface.querySelectorAll('form .hc-newsletter-form__message').forEach((message) => {
      if (!message.closest('hc-open-signup')) message.hidden = true;
    });
  });

  // Existing controls retain the drawer/menu lifecycle, focus and dismissal behavior.
  if (placement === 'desktop') {
    document.querySelector('[data-mega-menu-trigger][data-mega-menu-template="prints"]')?.click();
  }
}
restoreOpenSignupResponse();

class HcProductLightbox extends HTMLElement {
  connectedCallback() {
    this.triggers = Array.from(this.querySelectorAll('[data-hc-product-lightbox-trigger]'));
    this.dialog = this.querySelector('[data-hc-product-lightbox-dialog]');
    this.viewport = this.querySelector('[data-hc-product-lightbox-viewport]');
    this.image = this.querySelector('[data-hc-product-lightbox-image]');
    this.loading = this.querySelector('[data-hc-product-lightbox-loading]');
    this.level = this.querySelector('[data-hc-product-lightbox-level]');
    this.closeButton = this.querySelector('[data-hc-product-lightbox-close]');
    this.zoomInButton = this.querySelector('[data-hc-product-lightbox-zoom-in]');
    this.zoomOutButton = this.querySelector('[data-hc-product-lightbox-zoom-out]');
    this.resetButton = this.querySelector('[data-hc-product-lightbox-reset]');
    this.zoom = 1;
    this.minZoom = 1;
    this.maxZoom = 4;
    this.zoomStep = 0.5;
    this.wheelZoomStep = 0.25;
    this.activeTrigger = null;
    this.isDragging = false;
    this.dragStart = null;

    if (!this.triggers.length || !this.dialog || !this.image) return;

    this.triggers.forEach((trigger) => {
      trigger.addEventListener('click', () => this.open(trigger));
    });

    this.closeButton?.addEventListener('click', () => this.close());
    this.zoomInButton?.addEventListener('click', () => this.setZoom(this.zoom + this.zoomStep));
    this.zoomOutButton?.addEventListener('click', () => this.setZoom(this.zoom - this.zoomStep));
    this.resetButton?.addEventListener('click', () => this.setZoom(1));
    this.viewport?.addEventListener('wheel', (event) => this.handleWheel(event), { passive: false });
    this.viewport?.addEventListener('pointerdown', (event) => this.startPan(event));
    this.viewport?.addEventListener('pointermove', (event) => this.movePan(event));
    this.viewport?.addEventListener('pointerup', (event) => this.endPan(event));
    this.viewport?.addEventListener('pointercancel', (event) => this.endPan(event));
    this.viewport?.addEventListener('lostpointercapture', (event) => this.endPan(event));

    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.close();
    });

    this.dialog.addEventListener('close', () => {
      document.documentElement.classList.remove('hc-lightbox-open');
      this.activeTrigger?.focus();
      this.activeTrigger = null;
      this.image.removeAttribute('src');
      this.image.alt = '';
      this.setLoading(false);
      this.endPan();
    });

    this.image.addEventListener('load', () => {
      this.setLoading(false);
      this.applyZoom();
    });
    this.image.addEventListener('error', () => this.setLoading(false));
  }

  open(trigger) {
    let imageUrl = trigger.dataset.fullImageUrl;
    if (!imageUrl) return;
    if (imageUrl.startsWith('//')) imageUrl = `https:${imageUrl}`;

    this.activeTrigger = trigger;
    this.zoom = 1;
    this.image.alt = trigger.dataset.imageAlt || '';
    this.setLoading(true);
    this.image.src = imageUrl;
    this.applyZoom();
    document.documentElement.classList.add('hc-lightbox-open');

    if (typeof this.dialog.showModal === 'function') {
      this.dialog.showModal();
    } else {
      this.dialog.setAttribute('open', '');
    }

    this.closeButton?.focus();

    if (this.image.complete && this.image.naturalWidth > 0) {
      this.setLoading(false);
      this.applyZoom();
    }
  }

  close() {
    if (typeof this.dialog.close === 'function') {
      this.dialog.close();
      return;
    }

    this.dialog.removeAttribute('open');
    this.dialog.dispatchEvent(new Event('close'));
  }

  setZoom(value, options = {}) {
    this.zoom = Math.min(this.maxZoom, Math.max(this.minZoom, value));
    this.applyZoom({ center: options.center ?? true, point: options.point });
  }

  applyZoom({ center = false, point = null } = {}) {
    if (!this.image || !this.level) return;

    this.level.textContent = `${Math.round(this.zoom * 100)}%`;

    if (this.zoom === 1) {
      this.image.classList.remove('is-zoomed');
      this.image.style.width = '';
    } else {
      const naturalWidth = this.image.naturalWidth || this.image.clientWidth || 1000;
      this.image.classList.add('is-zoomed');
      this.image.style.width = `${Math.round(naturalWidth * this.zoom)}px`;
    }

    if (point && this.viewport) {
      window.requestAnimationFrame(() => {
        this.viewport.scrollLeft = Math.max((this.viewport.scrollWidth * point.xRatio) - point.x, 0);
        this.viewport.scrollTop = Math.max((this.viewport.scrollHeight * point.yRatio) - point.y, 0);
      });
    } else if (center && this.viewport) {
      window.requestAnimationFrame(() => {
        this.viewport.scrollLeft = Math.max((this.viewport.scrollWidth - this.viewport.clientWidth) / 2, 0);
        this.viewport.scrollTop = Math.max((this.viewport.scrollHeight - this.viewport.clientHeight) / 2, 0);
      });
    }
  }

  handleWheel(event) {
    if (!this.isDesktopPointer()) return;

    event.preventDefault();
    const zoomDelta = event.deltaY < 0 ? this.wheelZoomStep : -this.wheelZoomStep;
    const point = this.getZoomPoint(event.clientX, event.clientY);
    this.setZoom(this.zoom + zoomDelta, { center: false, point });
  }

  getZoomPoint(clientX, clientY) {
    if (!this.viewport) return null;

    const rect = this.viewport.getBoundingClientRect();
    return {
      x: clientX - rect.left,
      y: clientY - rect.top,
      xRatio: (this.viewport.scrollLeft + clientX - rect.left) / Math.max(this.viewport.scrollWidth, 1),
      yRatio: (this.viewport.scrollTop + clientY - rect.top) / Math.max(this.viewport.scrollHeight, 1)
    };
  }

  startPan(event) {
    if (!this.isDesktopPointer() || event.button !== 0 || !this.viewport) return;

    this.isDragging = true;
    this.dragStart = {
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      scrollLeft: this.viewport.scrollLeft,
      scrollTop: this.viewport.scrollTop
    };
    this.viewport.classList.add('is-panning');
    this.viewport.setPointerCapture(event.pointerId);
    event.preventDefault();
  }

  movePan(event) {
    if (!this.isDragging || !this.dragStart || !this.viewport) return;

    this.viewport.scrollLeft = this.dragStart.scrollLeft - (event.clientX - this.dragStart.x);
    this.viewport.scrollTop = this.dragStart.scrollTop - (event.clientY - this.dragStart.y);
  }

  endPan(event = null) {
    if (!this.isDragging) return;

    if (event?.pointerId !== undefined && this.viewport?.hasPointerCapture(event.pointerId)) {
      this.viewport.releasePointerCapture(event.pointerId);
    }

    this.isDragging = false;
    this.dragStart = null;
    this.viewport?.classList.remove('is-panning');
  }

  setLoading(isLoading) {
    if (!this.loading) return;

    this.loading.hidden = !isLoading;
    this.viewport?.classList.toggle('is-loading', isLoading);
  }

  isDesktopPointer() {
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  }
}

customElements.define('hc-product-lightbox', HcProductLightbox);

const initProductVariantMedia = (root) => {
  const select = root.querySelector('[data-hc-product-variant-select]');
  const mediaGrid = root.querySelector('[data-hc-product-media-grid]');
  const variantMediaScript = root.querySelector('[data-hc-product-variant-media]');

  if (!select || !mediaGrid || !variantMediaScript) return;

  let variantMedia = [];

  try {
    variantMedia = JSON.parse(variantMediaScript.textContent || '[]');
  } catch {
    return;
  }

  const mediaByVariant = new Map(
    variantMedia
      .filter((item) => item?.id && item?.src)
      .map((item) => [String(item.id), item])
  );

  if (!mediaByVariant.size) return;

  const getPrimaryMedia = () => (
    root.querySelector('[data-hc-product-primary-image]')
    || root.querySelector('[data-hc-product-media]')
  );

  const setPrimaryMedia = (mediaElement, media) => {
    if (!mediaElement || !media) return;

    root.querySelectorAll('[data-hc-product-primary-image]').forEach((item) => {
      item.removeAttribute('data-hc-product-primary-image');
    });
    mediaElement.setAttribute('data-hc-product-primary-image', '');

    const image = mediaElement.querySelector('img');
    if (!image) return;

    if (media.srcset) image.srcset = media.srcset;
    if (media.src) image.src = media.src;
    if (media.alt) image.alt = media.alt;
  };

  const updatePrimaryMedia = () => {
    const media = mediaByVariant.get(String(select.value));
    if (!media) return;

    const matchingMediaElement = media.mediaId
      ? root.querySelector(`[data-hc-product-media-id="${CSS.escape(String(media.mediaId))}"]`)
      : null;

    if (matchingMediaElement && matchingMediaElement !== mediaGrid.firstElementChild) {
      mediaGrid.insertBefore(matchingMediaElement, mediaGrid.firstElementChild);
    }

    setPrimaryMedia(matchingMediaElement || getPrimaryMedia(), media);
  };

  select.addEventListener('change', updatePrimaryMedia);
  updatePrimaryMedia();
};

document.querySelectorAll('[data-hc-product]').forEach(initProductVariantMedia);

const scrubCookieConsentCopy = () => {
  document.querySelectorAll('#shopify-pc__banner, #shopify-pc__prefs, [id^="shopify-pc__"]').forEach((root) => {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);

    nodes.forEach((node) => {
      node.nodeValue = node.nodeValue
        .replace(/,\s*including Shopify,/gi, '')
        .replace(/including Shopify,/gi, '')
        .replace(/including shopify,/gi, '')
        .replace(/\s{2,}/g, ' ');
    });
  });
};

scrubCookieConsentCopy();

new MutationObserver(scrubCookieConsentCopy).observe(document.documentElement, {
  childList: true,
  subtree: true,
});

document.querySelectorAll('.hc-localization__form').forEach((form) => {
  // Both language and country selectors keep the visitor on the current route.
  const controls = form.querySelectorAll('select[name="locale_code"], select[name="country_code"]');

  controls.forEach((control) => {
    // Use one submission path for desktop and mobile localization forms.
    control.addEventListener('change', () => {
      // Prevent duplicate requests while keeping the current filter/history state.
      if (form.dataset.submitting === 'true') return;
      form.dataset.submitting = 'true';
      const returnTo = form.querySelector('input[name="return_to"]');
      if (returnTo) returnTo.value = window.location.pathname + window.location.search + window.location.hash;

      // Retain native validation and the existing older-browser fallback.
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit();
      } else {
        form.submit();
      }
    });
  });
});

document.querySelectorAll('[data-browser-option-select]').forEach((control) => {
  control.addEventListener('change', () => {
    if (control.value) window.location.href = control.value;
  });
});

document.querySelectorAll('[data-browser-param-select]').forEach((control) => {
  control.addEventListener('change', () => {
    const param = control.dataset.browserParamSelect;
    if (!param) return;
    const url = new URL(window.location.href);
    if (control.value) {
      url.searchParams.set(param, control.value);
    } else {
      url.searchParams.delete(param);
    }
    url.searchParams.delete('page');
    window.location.href = url.toString();
  });
});

class HcLuxuryCollectionBrowser extends HTMLElement {
  connectedCallback() {
    // Cache the grid, filter controls, cards, and localized status elements.
    this.grid = this.querySelector('[data-luxury-grid]');
    if (!this.grid) return;
    this.cards = Array.from(this.querySelectorAll('[data-luxury-product]'));
    this.colorControls = Array.from(this.querySelectorAll('[data-dominant-color-filter]'));
    this.emptyMessage = this.querySelector('[data-luxury-empty]');
    this.countMessage = this.querySelector('[data-luxury-count]');
    this.colorSummary = this.querySelector('[data-color-summary]');
    this.restoreSelection = this.restoreSelection.bind(this);

    // Family emptiness is distinct from zero color matches or incomplete retrieval.
    this.familyEmpty = this.querySelector('[data-family-empty]');
    this.toolbar = this.querySelector('.hc-luxury-toolbar');
    this.hasMoreCards = Boolean(this.querySelector('[data-color-next]'));

    // Re-rank the complete result set whenever a selected paint chip changes.
    this.colorControls.forEach((control) => {
      // Delegate each checkbox change to the shared URL update.
      control.addEventListener('change', () => {
        // Preserve a visitor-recoverable history entry for each filter choice.
        this.saveSelection();
      });
    });
    // Wire both toolbar and empty-state reset controls.
    this.querySelectorAll('[data-color-reset]').forEach((button) => button.addEventListener('click', () => {
      // Reset optional colors while keeping the collection and unrelated parameters.
      this.colorControls.forEach((control) => { control.checked = false; });
      this.saveSelection();
    }));

    // Restore URL state without adding a new history entry.
    window.addEventListener('popstate', this.restoreSelection);
    this.restoreSelection();
    this.querySelector('[data-color-retry]')?.addEventListener('click', () => this.loadRemainingCards());
    this.loadRemainingCards();
  }

  disconnectedCallback() {
    // Remove global listeners when Shopify replaces a section.
    window.removeEventListener('popstate', this.restoreSelection);
  }

  restoreSelection() {
    // Reflect direct links and history navigation in native controls.
    const selected = new URL(window.location.href).searchParams.getAll('dominant_color');
    this.colorControls.forEach((control) => { control.checked = selected.includes(control.value); });
    this.applyFilters();
  }

  saveSelection() {
    // Keep family routing and all unrelated query parameters intact.
    const url = new URL(window.location.href);
    url.searchParams.delete('dominant_color');
    this.selectedColors().forEach((color) => url.searchParams.append('dominant_color', color));
    history.pushState(null, '', url);
    this.applyFilters();
  }

  async loadRemainingCards() {
    // Collect every Shopify page before reporting a complete filtered count.
    // Keep the normal next-page link usable if the network fails.
    const nextLink = this.querySelector('[data-color-next]');
    if (!nextLink || this.loadingCards) return;
    this.loadingCards = true;
    this.loadFailed = false;
    this.setAttribute('aria-busy', 'true');
    if (this.countMessage) this.countMessage.hidden = true;
    this.updateLoadState();

    // Retrieve the remaining pages without losing the resumable next-page link.
    try {
      let next = nextLink.href;
      while (next) {
        const response = await fetch(next);
        if (!response.ok) throw new Error('Product page unavailable');
        const documentPage = new DOMParser().parseFromString(await response.text(), 'text/html');
        const browser = documentPage.querySelector('hc-luxury-collection-browser');
        if (!browser) throw new Error('Product grid unavailable');

        // Append only complete, successfully parsed page contents.
        browser.querySelectorAll('[data-luxury-product]').forEach((card) => {
          // Register the fetched card in both the ranking set and DOM.
          this.cards.push(card);
          this.grid.appendChild(card);
        });

        // Advance the retry checkpoint after every successful page.
        const link = browser.querySelector('[data-color-next]');
        next = link ? new URL(link.getAttribute('href'), next).href : null;
        if (next) nextLink.href = next;
      }

      // Only a complete retrieval can expose the final result count.
      nextLink.remove();
      this.hasMoreCards = false;
      if (this.countMessage) this.countMessage.hidden = false;
    } catch (error) {
      // Do not misrepresent a partial result set as the complete catalog.
      nextLink.hidden = false;
      this.loadFailed = true;
    } finally {
      // Finish loading while preserving failure status for a recoverable retry.
      this.removeAttribute('aria-busy');
      this.loadingCards = false;
      this.updateLoadState();
      this.applyFilters();
    }
  }

  updateLoadState() {
    // Loading and failure are real retrieval states, never local-filter decorations.
    const loading = this.querySelector('[data-color-loading]');
    const error = this.querySelector('[data-color-error]');
    if (loading) loading.hidden = !this.loadingCards;
    if (error) error.hidden = !this.loadFailed;
    if (this.emptyMessage && (this.loadingCards || this.loadFailed)) this.emptyMessage.hidden = true;
  }

  selectedColors() {
    // Return the canonical color tokens from every selected native checkbox.
    return this.colorControls
      .filter((control) => control.checked)
      .map((control) => control.value);
  }

  scoreCard(card, selectedColors) {
    // Match exact family members, once per selected family, for any product type.
    const tokens = (card.dataset.productColors || '').split(/\s+/).filter(Boolean);
    const productColors = new Set(tokens);
    return selectedColors.reduce(
      (score, color) => {
        // Count a family once even when several aliases occur on the product.
        const control = this.colorControls.find((item) => item.value === color);
        const members = (control?.dataset.colorMembers || color).split(/\s+/);
        return score + (members.some((member) => productColors.has(member)) ? 1 : 0);
      },
      0,
    );
  }

  applyFilters() {
    // Describe selected family members without repeating overlapping shades.
    const selectedColors = this.selectedColors();
    if (this.colorSummary) {
      const labels = [...new Set(this.colorControls
        .filter((control) => control.checked)
        .flatMap((control) => (control.dataset.colorLabels || control.value).split('|')))];
      // Keep the textual selection summary synchronized with the checkboxes.
      this.colorSummary.hidden = labels.length === 0;
      this.colorSummary.textContent = labels.length
        ? this.colorSummary.dataset.summaryTemplate.replace('__COLORS__', labels.join(', '))
        : '';
    }

    // Require a match in every selected colour family.
    const rankedCards = this.cards.map((card) => ({
      card,
      score: this.scoreCard(card, selectedColors),
      index: Number(card.dataset.originalIndex),
    }));
    // Matching cards retain the underlying collection order.
    this.visibleCards = rankedCards
      .filter((item) => !selectedColors.length || item.score === selectedColors.length)
      .sort((a, b) => selectedColors.length ? b.score - a.score || a.index - b.index : a.index - b.index)
      .map((item) => item.card);

    // Keep every matching card visible and every non-match hidden.
    const visibleCards = new Set(this.visibleCards);
    this.cards.forEach((card) => {
      card.hidden = !visibleCards.has(card);
    });
    this.visibleCards.forEach((card) => this.grid.appendChild(card));

    // Reflect the filtered result count and localized empty state.
    if (this.countMessage) {
      const template = (this.visibleCards.length === 1 && this.countMessage.dataset.countOneTemplate)
        || this.countMessage.dataset.countTemplate || '__COUNT__';
      this.countMessage.textContent = template.replace('__COUNT__', this.visibleCards.length);
    }
    if (this.emptyMessage) this.emptyMessage.hidden = this.visibleCards.length > 0 || this.loadingCards || this.loadFailed;

    // Hide irrelevant controls only once all pages confirm the family is empty.
    if (this.familyEmpty) {
      const noEditions = !this.cards.length && !this.hasMoreCards && !this.loadFailed;
      this.familyEmpty.hidden = !noEditions;
      if (this.toolbar) this.toolbar.hidden = noEditions;
      if (this.countMessage) this.countMessage.hidden = noEditions || this.hasMoreCards || this.loadFailed;
      if (this.emptyMessage && (noEditions || this.hasMoreCards)) this.emptyMessage.hidden = true;
    }
  }
}

customElements.define('hc-luxury-collection-browser', HcLuxuryCollectionBrowser);

const header = document.querySelector('.hc-header');

if (header) {
  let lastScrollY = window.scrollY;
  let ticking = false;
  const syncHeaderHeight = () => {
    document.documentElement.style.setProperty('--hc-header-height', `${header.offsetHeight}px`);
  };

  const syncHeaderVisibility = () => {
    syncHeaderHeight();
    const currentScrollY = window.scrollY;
    const isDesktop = desktopQuery.matches;
    const headerHeight = header.offsetHeight;
    const scrollingDown = currentScrollY > lastScrollY;

    if (!isDesktop || currentScrollY <= 0) {
      header.classList.remove('is-scroll-revealed');
    } else if (scrollingDown) {
      header.classList.remove('is-scroll-revealed');
    } else {
      header.classList.add('is-scroll-revealed');
    }

    lastScrollY = Math.max(currentScrollY, 0);
    ticking = false;
  };

  syncHeaderHeight();

  window.addEventListener('scroll', () => {
    if (ticking) return;
    window.requestAnimationFrame(syncHeaderVisibility);
    ticking = true;
  }, { passive: true });

  window.addEventListener('resize', syncHeaderHeight);
  desktopQuery.addEventListener('change', syncHeaderVisibility);
}
